import {
  AtomicBlockUtils,
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
  Entity,
  Modifier,
  RawDraftContentState,
  RichUtils,
} from 'draft-js'
import { getEntityRange, getSelectionEntity } from 'draftjs-utils'
import get from 'lodash/get'
import merge from 'lodash/merge'
import type {
  Campaign,
  CampaignSection,
  ImageProps,
} from 'types/content-editor'
import {
  DEFAULT_FONT_FAMILY,
  ENTITY_TYPE,
  MERGE_TAG_TEMPLATE_VAR,
  MERGE_TAG_UI_TEXT,
} from '@disco/constants/content-editor'

const insertEntity = (
  editorState: EditorState,
  text: string,
  entity: {
    type: string
    mutability: 'MUTABLE' | 'IMMUTABLE'
    props: Record<string, any>
  },
): EditorState => {
  const entityKey = editorState
    .getCurrentContent()
    .createEntity(entity.type, entity.mutability, entity.props)
    .getLastCreatedEntityKey()
  const newEditorState = AtomicBlockUtils.insertAtomicBlock(
    editorState,
    entityKey,
    text,
  )
  return EditorState.forceSelection(
    newEditorState,
    newEditorState.getCurrentContent().getSelectionAfter(),
  )
}

export const insertImage = (
  editorState: EditorState,
  props?: {
    src: string
  },
): EditorState => {
  return insertEntity(editorState, ' ', {
    type: 'image',
    mutability: 'IMMUTABLE',
    props: props || {},
  })
}
export const insertMergeTag = (
  editorState: EditorState,
  props?: Record<string, any>,
): EditorState => {
  if (!props) return editorState
  const currentContent = editorState.getCurrentContent()
  const selection = editorState.getSelection()
  const newContent = currentContent.createEntity(
    ENTITY_TYPE.MERGE_TAG,
    'IMMUTABLE',
    props || {},
  )
  const entityKey = newContent.getLastCreatedEntityKey()
  const focusOffset = selection.getFocusOffset()
  const anchorOffset = selection.getAnchorOffset()
  const modifierFunc =
    focusOffset === anchorOffset ? Modifier.insertText : Modifier.replaceText
  const textWithEntity = modifierFunc(
    currentContent,
    selection,
    MERGE_TAG_UI_TEXT[props.type],
    editorState.getCurrentInlineStyle(),
    entityKey,
  )

  const displayMergeTag = () => {
    const event = new MouseEvent('click', {
      bubbles: true,
      view: window,
    })
    const mergeTag = document.getElementById(`merge-tag-${entityKey}`)

    if (mergeTag) {
      mergeTag.dispatchEvent(event)
    } else {
      setTimeout(displayMergeTag, 10)
    }
  }

  setTimeout(displayMergeTag, 10)
  return EditorState.push(editorState, textWithEntity, 'insert-characters')
}
export const insertButton = (
  editorState: EditorState,
  props?: {
    caption?: string
    link: string
    linkTarget?: string
    style?: Record<string, any>
  },
): EditorState => {
  const defaultProps = {
    caption: 'Button',
    linkTarget: '_blank',
    style: {
      backgroundColor: '#0693e3',
      // use defaults from https://mjml.io/documentation/#mjml-button
      // so button will look idential in app and in email
      borderRadius: '3px',
      lineHeight: '120%',
      padding: '10px 25px',
      color: 'white',
    },
  }
  const mergedProps = merge(defaultProps, props || {})
  return insertEntity(editorState, mergedProps.caption, {
    type: 'button',
    mutability: 'IMMUTABLE',
    props: mergedProps,
  })
}
export const insertLink = (
  editorState: EditorState,
  props?: {
    caption: string
    url: string
    target: string
  },
): EditorState => {
  if (!props) return editorState
  const currentContent = editorState.getCurrentContent()
  const selection = editorState.getSelection()
  const { url, target } = props
  const newContent = currentContent.createEntity('LINK', 'IMMUTABLE', {
    url,
    target,
  })
  const entityKey = newContent.getLastCreatedEntityKey()
  const focusOffset = selection.getFocusOffset()
  const anchorOffset = selection.getAnchorOffset()
  const modifierFunc =
    focusOffset === anchorOffset ? Modifier.insertText : Modifier.replaceText
  const textWithEntity = modifierFunc(
    currentContent,
    selection,
    get(props, 'caption', url),
    null,
    entityKey,
  )
  return EditorState.push(editorState, textWithEntity, 'insert-characters')
}
export const removeLink = (editorState: EditorState): EditorState => {
  const currentEntity = getSelectionEntity(editorState)
  let selection = editorState.getSelection()

  if (currentEntity) {
    const entityRange = getEntityRange(editorState, currentEntity)
    selection = selection.merge({
      anchorOffset: entityRange.start,
      focusOffset: entityRange.end,
    })
    return RichUtils.toggleLink(editorState, selection, null)
  }

  return editorState
}
export const getCurrentEntity = (
  editorState: EditorState,
): Entity | null | undefined => {
  const contentState = editorState.getCurrentContent()
  const entityKey = getSelectionEntity(editorState)
  return entityKey ? contentState.getEntity(entityKey) : null
}
export const getEntityTypeByEntityKey = (
  editorState: EditorState,
  entityKey: string | null | undefined,
): string | null | undefined => {
  const contentState = editorState.getCurrentContent()
  const entity = contentState.getEntity(entityKey)
  return entity ? entity.get('type').toUpperCase() : null
}
export const getMaxImageWidth = (
  section: CampaignSection | null | undefined,
  campaign: Campaign | null | undefined,
): number => {
  const { h } = getSectionPaddings(section, campaign)
  return section && section.columns.length > 1 ? 600 / 2 - h * 2 : 600 - h * 2
}
export const getFontFamily = (
  section: CampaignSection | null | undefined,
  campaign: Campaign,
): string =>
  (section && section.fontFamily) || campaign.fontFamily || DEFAULT_FONT_FAMILY
export const getFontSize = (
  section: CampaignSection | null | undefined,
  campaign: Campaign,
): string =>
  ((section && section.fontSize) || campaign.fontSize || '13').replace('px', '')

const getCombinedProp = (
  section: CampaignSection | null | undefined,
  campaign: Campaign | null | undefined,
  prop: string,
  defaultValue: string,
): string =>
  (section && section[prop]) || (campaign && campaign[prop]) || defaultValue

export const getSectionPaddings = (
  section: CampaignSection | null | undefined,
  campaign: Campaign | null | undefined,
): {
  h: number
  v: number
} => ({
  h: parseInt(
    getCombinedProp(section, campaign, 'horizontalPadding', '25'),
    10,
  ),
  v: parseInt(getCombinedProp(section, campaign, 'verticalPadding', '12'), 10),
})
export const getHeaderSize = (fontSize: string): string =>
  (parseFloat(fontSize.replace('px', '')) + 3).toString()

const isDiscoDomain = (src: string) => {
  return !!src.match(
    /https?:\/\/.*(disco.ac|dsc.party|localhost|\d+\.\d+\.\d+\.\d+)/i,
  )
}

export const getImageSrc = (image: ImageProps): string => {
  return image.height &&
    image.width &&
    isDiscoDomain(image.src) &&
    image.src.includes('/uploaded_image/')
    ? `${image.src}&mode=contain&width=${
        parseInt(image.width, 10) * 2
      }&height=${parseInt(image.height, 10) * 2}`
    : image.src
}
export const exportMergeTag = (entity: any): string => {
  const data = entity.getData()
  return `{{${MERGE_TAG_TEMPLATE_VAR[data.type]}|default:"${data.fallback}"}}`
}
export const getRawContentFromHTML = (html: string): RawDraftContentState => {
  const blocks = convertFromHTML(html)
  const contentState = ContentState.createFromBlockArray(
    blocks.contentBlocks,
    blocks.entityMap,
  )
  return convertToRaw(contentState)
}
export const getRawContentFromString = (
  text?: string,
): RawDraftContentState | null | undefined => {
  if (text) {
    return getRawContentFromHTML('<p>' + text + '</p>')
  }

  return null
}
